import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import {
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LocationPicker } from '../LocationPicker';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import CustomerPill from 'src/components/customer/CustomerPill';
import LocationPill from 'src/components/location/LocationPill';
import Icon from '@mui/material/Icon';
import useSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraft';
import useUpdateSalesOrderDraftField from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftField';
import useUpdateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftLineItem';
import useCreateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useCreateSalesOrderDraftLineItem';
import useDeleteSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraftLineItem';
import useApproveSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useApproveSalesOrderDraft';
import useRejectSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useRejectSalesOrderDraft';
import useDeleteSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraft';
import useUpdatePurchaseOrderCustomer from 'src/api/accounts-receivable/useUpdatePurchaseOrderCustomer';
import useMatchLineItemToProductVariant from 'src/api/inventory-management/products/useMatchLineItemToProductVariant';
import {
  useAddSalesOrderDraftCharge,
  useUpdateSalesOrderDraftCharge,
  useDeleteSalesOrderDraftCharge,
} from 'src/api/accounts-receivable/sales-order-drafts/useManageSalesOrderDraftCharges';
import AssociateLineItemModal from '../AssociateLineItemModal';
import LineItemsTable from '../line-item-table/LineItemsTable';
import DeleteLineItemModal from '../DeleteLineItemModal';
import RejectSalesOrderDraftModal from '../RejectSalesOrderDraftModal';
import ChargesSection from '../ChargesSection';
import SourceBadge from 'src/components/source-badge/SourceBadge';
import {
  SalesOrderSource,
  SalesOrderDraftStatus,
  LineItem,
  Charge,
} from 'src/types/application/SalesOrderDraftDTO';
import { SalesOrderDraftStatusBadge } from 'src/components/sales-order-draft-status-badge/SalesOrderDraftStatusBadge';
import EdiTransactionViewer from './EdiTransactionViewer';
import Watchers from 'src/components/watchers/Watchers';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { EmailData } from '@/components/email-viewer/email-viewer-types';
import { useToast } from '@/context/ToastContext';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import type { Column } from 'react-table';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { cn } from '@/lib/utils';
import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { QuestionIcon } from '@phosphor-icons/react';
import {
  //  AIWorkflow,
  SystemEntity,
} from '@erp/shared-types';
import { ReextractSalesOrderDraftDialog } from '@/components/purchase-orders/ReextractSalesOrderDraftDialog';
import { RobotRetryButton } from '@/components/common/robot-retry-button';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerProductDTO } from 'src/types/application/inventory-management/CustomerProductDTO';
import { ProductVariantDTO } from 'src/types/application/inventory-management/ProductVariantDTO';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { SalesOrderSyncStatus } from '@/types/application/SalesOrderDTOV2_DEPRECATED';
import OptionsMenu from './OptionsMenu';
import config from '@/config';
import { ModernPDFViewer } from '@/components/accounts-payable/invoice-detail/modern-pdf-viewer';
import { hasAddressData } from '@/utils/address-utils';
import {
  getSourceFlags,
  extractEmailDomain,
  formatDateOnly,
  parseDateOnly,
  isDatePast,
} from './utils';
import type { ValidationError } from './types';
import { SyncErrorDialog } from './SyncErrorDialog';
import { SourceDocumentViewer } from './SourceDocumentViewer';
import { ActivityGraph } from '@/components/activity-graph';

const showChargeModal = false;
const showReextractButton = false;

interface Props {
  salesOrderDraftId: string;
  onUpdate?: () => void;
  onClose: (refetchPOs?: boolean) => void;
  editable: boolean;
  navigationInfo?: {
    currentIndex: number;
    totalItems: number;
    onNavigate: (direction: 'prev' | 'next') => void;
  };
  isLoading: boolean;
  onSubModalStateChange?: (isAnySubModalOpen: boolean) => void;
  onSalesOrderCreated?: (salesOrderId: string) => void;
}

const SalesOrderDraftModal: React.FC<Props> = ({
  salesOrderDraftId,
  onUpdate,
  onClose,
  editable,
  navigationInfo,
  isLoading: _isLoading,
  onSubModalStateChange,
  onSalesOrderCreated,
}) => {
  const navigate = useNavigate({ from: '/sales-orders' });
  const queryClient = useQueryClient();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const { enabled: isMemoryBankNotesEnabled } = useFeatureFlag(
    'memory-bank-notes-enabled',
    false,
  );

  const { enabled: isActivityLogEnabled } = useFeatureFlag(
    'activity-log-enabled',
    false,
  );

  const {
    data: salesOrderDraftData,
    isLoading: _isSalesOrderDraftLoading,
    refetch: refetchSalesOrderDraft,
  } = useSalesOrderDraft(salesOrderDraftId);

  const salesOrderDraft = salesOrderDraftData?.stagedPurchaseOrder;

  const customerId =
    salesOrderDraft?.customerId || salesOrderDraft?.suggestedCustomerId;

  const customerName =
    salesOrderDraft?.customer?.erpDisplayName ||
    salesOrderDraft?.customer?.name;

  const sourceFlags = getSourceFlags(salesOrderDraft?.source);
  const { isEdiSource, isEmailSource, isDirectEntry, isFileUpload } =
    sourceFlags;

  // Check if sales order draft is already processed (approved or rejected)
  const isProcessed =
    salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED ||
    salesOrderDraft?.status === SalesOrderDraftStatus.REJECTED;

  // Modal should only be editable if editable prop is true AND the PO isn't already processed
  const isModalEditable = editable && !isProcessed;

  // Determine if actions should be shown (only show for non-processed and editable POs)
  const showActions = editable && !isProcessed;

  const customerEmailDomain = useMemo(
    () =>
      salesOrderDraft?.customer?.emailDomain ||
      salesOrderDraft?.suggestedCustomer?.emailDomain ||
      extractEmailDomain(salesOrderDraft?.sourceEmail?.from),
    [
      salesOrderDraft?.customer?.emailDomain,
      salesOrderDraft?.suggestedCustomer?.emailDomain,
      salesOrderDraft?.sourceEmail?.from,
    ],
  );

  // State for right panel visibility - default based on source
  const [isRightPanelVisible, setIsRightPanelVisible] = useState<boolean>(
    () => true, // Always show right panel by default regardless of source
  );

  // State for error dialog
  const [syncErrorDialogOpen, setSyncErrorDialogOpen] = useState(false);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | null>(null);
  const [erpSystemName, setErpSystemName] = useState<string | null>(null);

  const approveStagedPurchaseOrder = useApproveSalesOrderDraft(
    salesOrderDraftId,
    ({ salesOrderId, originSyncStatus }) => {
      // Notify parent about the created sales order
      onSalesOrderCreated?.(salesOrderId);

      // Close the draft modal
      onClose(true);

      // If sync failed, navigate to the sales order page
      if (originSyncStatus !== SalesOrderSyncStatus.success) {
        navigate({
          to: '/sales-orders/$salesOrderId',
          params: { salesOrderId },
        });
      }
    },
    (error) => {
      // Check if this is an ERP-specific sync error
      // The backend sends erpSystemName and erpErrorDetails for ERP errors
      const errorData = error as {
        message?: string;
        erpSystemName?: string;
        erpErrorDetails?: string;
      };

      if (errorData.erpSystemName) {
        // Show ERP-specific error dialog with detailed error info
        const displayMessage =
          errorData.erpErrorDetails || errorData.message || String(error);
        setErpSystemName(errorData.erpSystemName);
        setSyncErrorMessage(displayMessage);
        setSyncErrorDialogOpen(true);
      }
      // If not an ERP error, let the default error handling take over (toast, etc.)
    },
  );

  const rejectStagedPurchaseOrder = useRejectSalesOrderDraft(
    salesOrderDraftId,
    () => {
      onClose(true);
    },
  );

  const deleteStagedPurchaseOrder = useDeleteSalesOrderDraft(
    salesOrderDraftId,
    () => {
      onClose(true);
    },
  );

  const updateStagedPurchaseOrderLineItem = useUpdateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      // On success: clear optimistic updates and pending updates, then refetch
      setOptimisticUpdates({});
      setPendingUpdates({});
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const createLineItem = useCreateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteLineItem = useDeleteSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  // Charge management hooks
  const addOrderCharge = useAddSalesOrderDraftCharge(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updateOrderCharge = useUpdateSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteOrderCharge = useDeleteSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const matchLineItemToProductVariant = useMatchLineItemToProductVariant(() => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updatePurchaseOrderField = useUpdateSalesOrderDraftField(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const updateCustomer = useUpdatePurchaseOrderCustomer(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
      // Invalidate watchers query since changing customer updates assignees
      queryClient.invalidateQueries({
        queryKey: [`core/watches/staged_purchase_order/${salesOrderDraftId}`],
      });
    },
  );

  const handleCustomerSelect = useCallback(
    (customerId: string | undefined) => {
      if (customerId) {
        updateCustomer.mutateAsync({ customerId });
      }
    },
    [updateCustomer],
  );

  // TODO
  // const addLineItemCharge = useAddStagedPurchaseOrderLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // const updateLineItemCharge = useUpdateSalesOrderDraftLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // const deleteLineItemCharge = useDeleteSalesOrderDraftLineItemCharge(
  //   salesOrderDraftId,
  //   () => {
  //     onUpdate?.();
  //     refetchSalesOrderDraft();
  //   },
  // );

  // Convert InboundEmailDTO to EmailData with proper typing
  const emailData: EmailData = salesOrderDraft?.sourceData?.email
    ? {
        ...inboundEmailToEmailData(salesOrderDraft.sourceData.email),
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      }
    : {
        inboundEmailId: undefined,
        subject: undefined,
        from: undefined,
        to: undefined,
        htmlContent: undefined,
        textContent: undefined,
        attachments: undefined,
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      };

  const ediData = {
    ordefulMessage: salesOrderDraft?.sourceData?.orderfulJson,
  };

  const documentData = {
    document: salesOrderDraft?.sourceData?.document,
  };

  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number>();
  const [selectedLineItemSnapshot, setSelectedLineItemSnapshot] =
    useState<LineItem>();
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [showShipDateModal, setShowShipDateModal] = useState(false);
  // Inline editing state for PO number
  const [isEditingPurchaseOrderNumber, setIsEditingPurchaseOrderNumber] =
    useState(false);
  const [purchaseOrderNumberInput, setPurchaseOrderNumberInput] = useState('');
  const [showDeleteLineItemModal, setShowDeleteLineItemModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReextractDialog, setShowReextractDialog] = useState(false);
  const [deletingLineItem, setDeletingLineItem] = useState<
    LineItem | undefined
  >();
  const [deletingLineItemIndex, setDeletingLineItemIndex] = useState<
    number | undefined
  >();
  const { showToast } = useToast();

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationError>({});

  // Optimistic updates tracking
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<number, Partial<LineItem>>
  >({});

  // Track which fields are currently being updated (for visual feedback)
  const [pendingUpdates, setPendingUpdates] = useState<
    Record<
      number,
      Set<
        | 'quantity'
        | 'unitPrice'
        | 'rescheduledDate'
        | 'customerDescription'
        | 'customerIdentifier'
      >
    >
  >({});

  const lineItems: LineItem[] = useMemo(
    () => salesOrderDraft?.lineItems || [],
    [salesOrderDraft?.lineItems],
  );

  // Apply optimistic updates to line items for display
  const displayLineItems = useMemo(
    () =>
      lineItems.map((item, index) => ({
        ...item,
        ...optimisticUpdates[index],
      })),
    [lineItems, optimisticUpdates],
  );

  const handleAssociateVariantSelected = useCallback(
    (rowIndex: number, item: LineItem, productVariantId?: string) => {
      if (!productVariantId || !customerId) return;
      matchLineItemToProductVariant.mutate({
        stagedCustomerPurchaseOrderId: salesOrderDraftId,
        stagedCustomerPurchaseOrderLineItemIndex: rowIndex,
        customerId,
        productVariantId,
        customerDescription:
          item.customerDescription || item.itemName || item.name,
        customerMainIdentifier: item.customerIdentifier || item.sku || item.upc,
      });
    },
    [customerId, matchLineItemToProductVariant, salesOrderDraftId],
  );

  // Use snapshot for modal display to prevent infinite re-renders
  const selectedLineItem = selectedLineItemSnapshot;

  // CRUD handlers for line items
  const handleAddLineItem = () => {
    window.alert('TODO: Add line item');
  };

  const handleUpdateLineItem = (
    index: number,
    field:
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier',
    value: number | string,
  ) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    // Apply optimistic update immediately
    setOptimisticUpdates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
      },
    }));

    // Mark field as pending
    setPendingUpdates((prev) => ({
      ...prev,
      [index]: new Set([...(prev[index] || []), field]),
    }));

    const updatedLineItem = {
      ...lineItem,
      [field]: value,
    };

    updateStagedPurchaseOrderLineItem.mutate(
      {
        index,
        before: lineItem,
        after: updatedLineItem,
      },
      {
        onError: () => {
          // On error: revert the optimistic update and clear pending state
          setOptimisticUpdates((prev) => {
            const newUpdates = { ...prev };
            if (newUpdates[index]) {
              delete newUpdates[index][field as keyof LineItem];
              if (Object.keys(newUpdates[index]).length === 0) {
                delete newUpdates[index];
              }
            }
            return newUpdates;
          });

          setPendingUpdates((prev) => {
            const newPending = { ...prev };
            if (newPending[index]) {
              newPending[index].delete(field);
              if (newPending[index].size === 0) {
                delete newPending[index];
              }
            }
            return newPending;
          });
        },
      },
    );
  };

  const handleDeleteLineItem = (index: number) => {
    const lineItem = lineItems[index];
    setDeletingLineItem(lineItem);
    setDeletingLineItemIndex(index);
    setShowDeleteLineItemModal(true);
  };

  const handleToggleRejectLineItem = (index: number) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    const updatedLineItem = {
      ...lineItem,
      ediRejected: !lineItem.ediRejected,
    };

    updateStagedPurchaseOrderLineItem.mutate({
      index,
      before: lineItem,
      after: updatedLineItem,
    });
  };

  const handleConfirmDelete = () => {
    if (deletingLineItemIndex !== undefined) {
      deleteLineItem.mutate({ index: deletingLineItemIndex });
      setShowDeleteLineItemModal(false);
      setDeletingLineItem(undefined);
      setDeletingLineItemIndex(undefined);
    }
  };

  // Feature flag to switch to AssociateProductV2 flow
  const { enabled: isAssociateProductV2Enabled } = useFeatureFlag(
    'associate-product-v2-enabled',
    false,
  );

  // Familiar line-item table to pass into AssociateProductV2 as children
  const childLineItemColumns: Column<LineItem>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'customerIdentifier',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original as LineItem;
          const identifier = item.customerIdentifier || item.sku || item.upc;
          return identifier ? (
            <MDTypography variant="caption">{identifier}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        },
      },
      {
        Header: 'Item',
        accessor: 'customerDescription',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original as LineItem;
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            'No description available';
          return (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text.secondary">
                {description}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: 'Unit Price',
        accessor: 'unitPrice',
        Cell: ({ value }: { value?: number | null }) => {
          return <>{value ? `$${Number(value).toFixed(2)}` : ''}</>;
        },
      },
    ],
    [],
  );

  // Legacy-only helper for old modal path
  const _childTableData = useMemo(
    () => ({
      columns: childLineItemColumns,
      rows: selectedLineItem ? [selectedLineItem] : [],
    }),
    [childLineItemColumns, selectedLineItem],
  );

  const shouldShowAssociateModal =
    selectedLineItemIndex !== undefined && !!selectedLineItem && !!customerId;

  const handleVariantSelected = useCallback(
    ({ productVariantId }: { productVariantId?: string }) => {
      if (productVariantId) {
        if (
          selectedLineItemIndex !== undefined &&
          customerId &&
          selectedLineItem
        ) {
          matchLineItemToProductVariant.mutate({
            stagedCustomerPurchaseOrderId: salesOrderDraftId,
            stagedCustomerPurchaseOrderLineItemIndex: selectedLineItemIndex,
            customerId,
            productVariantId,
            customerDescription:
              selectedLineItem.customerDescription ||
              selectedLineItem.itemName ||
              selectedLineItem.name,
            customerMainIdentifier:
              selectedLineItem.customerIdentifier ||
              selectedLineItem.sku ||
              selectedLineItem.upc,
          });
        } else {
          if (!customerId) {
            window.alert(
              'Customer ID is required to associate a line item to a product variant.',
            );
          } else {
            window.alert(
              'Something went wrong. Please contact the Gently team.',
            );
          }
        }
      }
      // Clear selection and snapshot
      setSelectedLineItemIndex(undefined);
      setSelectedLineItemSnapshot(undefined);
    },
    [
      selectedLineItemIndex,
      customerId,
      selectedLineItem,
      matchLineItemToProductVariant,
      salesOrderDraftId,
    ],
  );

  const renderAssociateModal = useCallback(() => {
    if (!shouldShowAssociateModal) return null;

    if (isAssociateProductV2Enabled) {
      // When the new flow is enabled, the popover trigger is rendered inline in the table cell.
      return null;
    }

    return (
      <AssociateLineItemModal
        lineItem={selectedLineItem}
        customerId={customerId}
        editable={isModalEditable}
        onClose={(productVariantId) => {
          handleVariantSelected({ productVariantId });
        }}
      />
    );
  }, [
    shouldShowAssociateModal,
    isAssociateProductV2Enabled,
    customerId,
    selectedLineItem,
    isModalEditable,
    handleVariantSelected,
  ]);

  // Charge handlers
  const handleAddOrderCharge = (charge: Charge) => {
    addOrderCharge.mutate({ charge });
  };

  const handleUpdateOrderCharge = (charge: Charge) => {
    if (charge.id) {
      updateOrderCharge.mutate({ charge, chargeId: charge.id });
    }
  };

  const handleDeleteOrderCharge = (chargeId: string) => {
    deleteOrderCharge.mutate({ chargeId });
  };

  const handleInlineShipDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDateOnly = parsed;
    const delivery = parseDateOnly(salesOrderDraft?.deliveryDate);
    if (delivery && nextDateOnly > delivery) {
      showToast({
        title: 'Invalid date',
        message: 'Ship date must be on or before the delivery date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'shipDate',
      value: formatDateOnly(nextDateOnly),
    });
  };

  const handleInlineDeliveryDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDate = parsed;
    const ship = parseDateOnly(salesOrderDraft?.shipDate);
    if (ship && nextDate < ship) {
      showToast({
        title: 'Invalid date',
        message: 'Delivery date must be on or after the ship date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'deliveryDate',
      value: formatDateOnly(nextDate),
    });
  };

  const updateField = useUpdateSalesOrderDraftField(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
    setShowDeliveryDateModal(false);
    setShowShipDateModal(false);
    setIsEditingPurchaseOrderNumber(false);
  });

  const isLoading =
    _isLoading ||
    _isSalesOrderDraftLoading ||
    approveStagedPurchaseOrder.isLoading ||
    rejectStagedPurchaseOrder.isLoading ||
    deleteStagedPurchaseOrder.isLoading ||
    updateStagedPurchaseOrderLineItem.isLoading ||
    createLineItem.isLoading ||
    deleteLineItem.isLoading ||
    matchLineItemToProductVariant.isLoading;

  // Notify parent when any sub-modals are open
  useEffect(() => {
    const isAnySubModalOpen =
      selectedLineItemIndex !== undefined ||
      showDeliveryDateModal ||
      showShipDateModal ||
      showDeleteLineItemModal ||
      showRejectModal ||
      showDeleteModal ||
      showReextractDialog;
    onSubModalStateChange?.(isAnySubModalOpen);
  }, [
    selectedLineItemIndex,
    showDeliveryDateModal,
    showShipDateModal,
    showDeleteLineItemModal,
    showRejectModal,
    showDeleteModal,
    showReextractDialog,
    onSubModalStateChange,
  ]);

  // Update right panel visibility when source changes
  useEffect(() => {
    setIsRightPanelVisible(true); // Always show right panel for all sources
  }, [isEdiSource]);

  // Clear optimistic updates when sales order draft data changes (e.g., from refetch)
  useEffect(() => {
    setOptimisticUpdates({});
  }, [salesOrderDraft?.lineItems]);

  // Set initial PO number input value when modal opens
  useEffect(() => {
    if (isEditingPurchaseOrderNumber) {
      setPurchaseOrderNumberInput(salesOrderDraft?.purchaseOrderNumber || '');
    }
  }, [isEditingPurchaseOrderNumber, salesOrderDraft?.purchaseOrderNumber]);

  // Clear validation errors and hook errors when sales order draft changes
  // useEffect(() => {
  //   setValidationErrors({});

  //   // Reset all mutation hook errors
  //   approveStagedPurchaseOrder.reset();
  //   rejectStagedPurchaseOrder.reset();
  //   updateStagedPurchaseOrderLineItem.reset();
  //   createLineItem.reset();
  //   deleteLineItem.reset();
  //   matchLineItemToProductVariant.reset();
  //   updatePurchaseOrderField.reset();
  //   updateField.reset();
  // }, [salesOrderDraftId]);

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleConfirmReject = () => {
    rejectStagedPurchaseOrder.mutate();
    setShowRejectModal(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteDraft = () => {
    deleteStagedPurchaseOrder.mutate();
    setShowDeleteModal(false);
  };

  const handleToggleRightPanel = () => {
    setIsRightPanelVisible(!isRightPanelVisible);
  };

  // Validation logic
  const validateForm = () => {
    const errors: ValidationError = {};

    console.log('Validating form:', {
      isEmailSource,
      shipToLocationId: salesOrderDraft?.shipToLocationId,
      billToLocationId: salesOrderDraft?.billToLocationId,
      buyingPartyLocationId: salesOrderDraft?.buyingPartyLocationId,
      source: salesOrderDraft?.source,
    });

    // Check if shipping location is required (for all sources)
    if (!salesOrderDraft?.shipToLocationId) {
      errors.shippingLocation = 'Shipping location is required';
      console.log('Validation failed: missing shipping location');
    }

    const inactiveProducts = salesOrderDraft?.lineItems?.filter((lineItem) => {
      const customerProductId = lineItem.selectedCustomerProductId;
      const variantId = lineItem.selectedProductVariantId;

      // Check customer product cache first
      const customerProductData = queryClient.getQueryData<{
        customerProduct: CustomerProductDTO;
      }>([
        `inventory-management/products-v2/customer-products/${customerProductId}`,
        undefined,
      ]);

      if (customerProductData) {
        return (
          customerProductData.customerProduct.productVariant.active === false
        );
      }

      // Fall back to product variant cache
      const variantData = queryClient.getQueryData<{
        productVariant: ProductVariantDTO;
      }>([
        `inventory-management/products-v2/product-variants/${variantId}`,
        undefined,
      ]);

      return variantData?.productVariant?.active === false;
    });

    if (inactiveProducts?.length) {
      errors.inactiveProducts = `All selected products must be active. Inactive products: ${inactiveProducts
        .map((item) => {
          return item.customerDescription || item.itemName || item.name;
        })
        .join(', ')}`;
    }

    setValidationErrors(errors);
    console.log('Validation errors:', errors);
    return Object.keys(errors).length === 0;
  };

  // Handle shipping location selection
  const handleLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'shipToLocationId',
        value: locationId,
      });
      // Clear validation error when location is selected
      setValidationErrors((prev) => ({ ...prev, shippingLocation: undefined }));
    }
  };

  // Handle bill to location selection
  const handleBillToLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'billToLocationId',
        value: locationId,
      });
      // Clear validation error when location is selected
      setValidationErrors((prev) => ({ ...prev, billToLocation: undefined }));
    }
  };

  // Handle buying party location selection
  const handleBuyingPartyLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'buyingPartyLocationId',
        value: locationId,
      });
      // Clear validation error when location is selected
      setValidationErrors((prev) => ({
        ...prev,
        buyingPartyLocation: undefined,
      }));
    }
  };

  // Handle approve with validation
  const handleApprove = () => {
    console.log('handleApprove called');
    const isValid = validateForm();
    console.log('Form is valid:', isValid);
    if (isValid) {
      console.log('Proceeding with approval');
      approveStagedPurchaseOrder.mutate();
    } else {
      console.log('Form validation failed, not proceeding with approval');
    }
  };

  return (
    <Modal
      maxWidth={'xl'}
      fullScreenable
      disableContentOverflow
      open={true}
      showAiAgentButton
      optionsMenu={
        config.isDev && isEdiSource ? (
          <OptionsMenu
            salesOrderDraftId={salesOrderDraftId}
            onRefresh={refetchSalesOrderDraft}
          />
        ) : undefined
      }
      title={
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDTypography variant="h6">
              {salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED
                ? 'Sales Order'
                : 'Sales Order Draft'}
            </MDTypography>
            <Watchers
              watchableEntityType="staged_purchase_order"
              watchableId={salesOrderDraftId}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={2}>
            {navigationInfo && (
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDBox
                  onClick={() => navigationInfo.onNavigate('prev')}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  }}
                >
                  <Icon>keyboard_arrow_left</Icon>
                </MDBox>
                <MDTypography variant="body2">
                  {navigationInfo.currentIndex + 1} of{' '}
                  {navigationInfo.totalItems}
                </MDTypography>
                <MDBox
                  onClick={() => navigationInfo.onNavigate('next')}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  }}
                >
                  <Icon>keyboard_arrow_right</Icon>
                </MDBox>
              </MDBox>
            )}
            {salesOrderDraft?.status && (
              <SalesOrderDraftStatusBadge status={salesOrderDraft.status} />
            )}
          </MDBox>
        </MDBox>
      }
      closeable
      isLoading={isLoading}
      error={
        approveStagedPurchaseOrder.error ||
        rejectStagedPurchaseOrder.error ||
        deleteStagedPurchaseOrder.error ||
        updateStagedPurchaseOrderLineItem.error ||
        matchLineItemToProductVariant.error
      }
      onCloseButton={{
        text: 'Close',
        onClick: onClose,
        disabled: approveStagedPurchaseOrder.isLoading,
      }}
      hideCloseButton
      onConfirmButton={
        showActions
          ? {
              text: 'Approve and Create Sales Order',
              disabled:
                isLoading || salesOrderDraft?.isReadyForApproval === false,
              onClick: handleApprove,
            }
          : undefined
      }
      additionalButtons={
        showActions
          ? [
              ...(isAdmin(currentUser, viewMode)
                ? [
                    {
                      text: 'Delete',
                      variant: 'text' as const,
                      color: 'error' as const,
                      onClick: handleDelete,
                      disabled: isLoading,
                    },
                  ]
                : []),
              {
                text: 'Ignore',
                variant: 'text',
                color: 'error',
                onClick: handleReject,
                disabled: isLoading,
              },
            ]
          : undefined
      }
    >
      <MDBox sx={{ height: '100%', overflow: 'hidden' }}>
        {salesOrderDraft ? (
          <Grid
            container
            spacing={2}
            sx={{ height: '100%', overflow: 'hidden' }}
          >
            {/* Left side - PO Info & Line Items */}
            <Grid
              item
              xs={12}
              md={isRightPanelVisible ? (isActivityLogEnabled ? 7 : 6) : 12}
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <MDBox mb={2} sx={{ flexShrink: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '140px 1fr',
                        gap: 1,
                        '& .label': {
                          fontWeight: 'bold',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <MDTypography className="label">
                          Customer PO #:
                        </MDTypography>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <QuestionIcon className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>
                                The purchase order number provided by the
                                customer. This helps match incoming orders to
                                customer records and invoices.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="flex items-center justify-between">
                        {isModalEditable ? (
                          isEditingPurchaseOrderNumber ? (
                            <TextField
                              size="small"
                              value={purchaseOrderNumberInput}
                              onChange={(e) =>
                                setPurchaseOrderNumberInput(e.target.value)
                              }
                              onBlur={() => {
                                const trimmed = purchaseOrderNumberInput.trim();
                                if (
                                  trimmed &&
                                  trimmed !==
                                    salesOrderDraft?.purchaseOrderNumber
                                ) {
                                  updateField.mutate({
                                    field: 'purchaseOrderNumber',
                                    value: trimmed,
                                  });
                                }
                                setIsEditingPurchaseOrderNumber(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const trimmed =
                                    purchaseOrderNumberInput.trim();
                                  if (trimmed) {
                                    updateField.mutate({
                                      field: 'purchaseOrderNumber',
                                      value: trimmed,
                                    });
                                  }
                                  setIsEditingPurchaseOrderNumber(false);
                                } else if (e.key === 'Escape') {
                                  setIsEditingPurchaseOrderNumber(false);
                                }
                              }}
                              autoFocus
                              sx={{
                                minWidth: '120px',
                                '& .MuiInputBase-input': {
                                  fontSize: '0.875rem',
                                  padding: '4px 8px',
                                },
                              }}
                            />
                          ) : salesOrderDraft.purchaseOrderNumber ? (
                            <MDTypography
                              variant="body2"
                              sx={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                setIsEditingPurchaseOrderNumber(true)
                              }
                            >
                              {salesOrderDraft.purchaseOrderNumber}
                            </MDTypography>
                          ) : (
                            <MDBox
                              onClick={() =>
                                setIsEditingPurchaseOrderNumber(true)
                              }
                              sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Icon color="error" sx={{ fontSize: 20 }}>
                                warning
                              </Icon>
                              <MDTypography variant="caption" color="error">
                                Missing PO Number
                              </MDTypography>
                            </MDBox>
                          )
                        ) : salesOrderDraft.purchaseOrderNumber ? (
                          <MDTypography variant="body2">
                            {salesOrderDraft.purchaseOrderNumber}
                          </MDTypography>
                        ) : (
                          <MDBox
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Icon color="error" sx={{ fontSize: 20 }}>
                              warning
                            </Icon>
                            <MDTypography variant="caption" color="error">
                              Missing PO Number
                            </MDTypography>
                          </MDBox>
                        )}
                      </div>

                      <MDTypography className="label">Source:</MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <SourceBadge
                          source={salesOrderDraft.source}
                          onClick={handleToggleRightPanel}
                        />
                        {showReextractButton &&
                          isEmailSource &&
                          emailData.inboundEmailId && (
                            <RobotRetryButton
                              onClick={() => setShowReextractDialog(true)}
                              tooltip="Re-extract Sales Order Draft"
                            >
                              Re-extract
                            </RobotRetryButton>
                          )}
                      </MDBox>

                      <MDBox className="label">Customer:</MDBox>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <MDBox sx={{ flex: 1, minWidth: 0 }}>
                          {isModalEditable ? (
                            <CustomersAutocomplete
                              selectedCustomerId={salesOrderDraft.customer?.id}
                              onCustomerSelect={handleCustomerSelect}
                              allowClear={false}
                              className="w-full"
                            />
                          ) : salesOrderDraft.customer ? (
                            <CustomerPill
                              customer={{
                                name:
                                  salesOrderDraft.customer.erpDisplayName ??
                                  salesOrderDraft.customer.name,
                                emailDomain:
                                  salesOrderDraft.customer.emailDomain,
                                phoneNumber:
                                  salesOrderDraft.customer.phoneNumber,
                              }}
                            />
                          ) : (
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              No customer assigned
                            </MDTypography>
                          )}
                        </MDBox>
                        {isMemoryBankNotesEnabled && customerId && (
                          <MemoryBankNotes
                            staticMetadata={{
                              // workflow: AIWorkflow.EMAIL_SO_INGESTION,
                              workflowStep: 'EXTRACT_SALES_ORDER',
                            }}
                            dynamicMetadata={{
                              customerEmailDomain,
                            }}
                            entityId={customerId}
                            entityType={SystemEntity.AR_CUSTOMER}
                            onNotesChange={refetchSalesOrderDraft}
                            tooltipText="Add notes to guide the AI when processing orders from this customer"
                          />
                        )}
                      </MDBox>

                      <MDBox className="label">Ship To Location:</MDBox>
                      {customerId ? (
                        isModalEditable ? (
                          <LocationPicker
                            customerId={customerId}
                            selectedLocationId={
                              salesOrderDraft.shipToLocationId
                            }
                            onLocationSelect={handleLocationSelect}
                            error={validationErrors.shippingLocation}
                          />
                        ) : salesOrderDraft.shipToLocationId ? (
                          <LocationPill
                            locationId={salesOrderDraft.shipToLocationId}
                            customerId={customerId}
                          />
                        ) : (
                          <MDTypography variant="body2" color="text.secondary">
                            No location assigned
                          </MDTypography>
                        )
                      ) : (
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDTypography variant="body2">
                            Please select a customer
                          </MDTypography>
                        </MDBox>
                      )}

                      {isEdiSource &&
                      hasAddressData(salesOrderDraft.billToAddress) ? (
                        <>
                          <MDBox className="label">Bill To Location:</MDBox>
                          {customerId ? (
                            isModalEditable ? (
                              <LocationPicker
                                customerId={customerId}
                                selectedLocationId={
                                  salesOrderDraft.billToLocationId
                                }
                                onLocationSelect={handleBillToLocationSelect}
                                error={validationErrors.billToLocation}
                              />
                            ) : salesOrderDraft.billToLocationId ? (
                              <LocationPill
                                locationId={salesOrderDraft.billToLocationId}
                                customerId={customerId}
                              />
                            ) : (
                              <MDTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                No location assigned
                              </MDTypography>
                            )
                          ) : (
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography variant="body2">
                                Please select a customer
                              </MDTypography>
                            </MDBox>
                          )}
                        </>
                      ) : null}

                      {isEdiSource &&
                      hasAddressData(salesOrderDraft.buyingPartyAddress) ? (
                        <>
                          <MDBox className="label">
                            Buying Party Location:
                          </MDBox>
                          {customerId ? (
                            isModalEditable ? (
                              <LocationPicker
                                customerId={customerId}
                                selectedLocationId={
                                  salesOrderDraft.buyingPartyLocationId
                                }
                                onLocationSelect={
                                  handleBuyingPartyLocationSelect
                                }
                                error={validationErrors.buyingPartyLocation}
                              />
                            ) : salesOrderDraft.buyingPartyLocationId ? (
                              <LocationPill
                                locationId={
                                  salesOrderDraft.buyingPartyLocationId
                                }
                                customerId={customerId}
                              />
                            ) : (
                              <MDTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                No location assigned
                              </MDTypography>
                            )
                          ) : (
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography variant="body2">
                                Please select a customer
                              </MDTypography>
                            </MDBox>
                          )}
                        </>
                      ) : null}

                      {/* <MDTypography className="label">
                        Credit Limit:
                      </MDTypography>
                      {salesOrderDraft.customer ? (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          width="100%"
                          maxWidth="300px"
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <MDTypography variant="caption">
                              $
                              {(
                                15000 +
                                parseFloat(salesOrderDraft.totalAmount || '0')
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </MDTypography>
                            <MDTypography variant="caption">
                              $50,000.00
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            sx={{
                              width: '100%',
                              height: '6px',
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '3px',
                              overflow: 'hidden',
                            }}
                          >
                            <MDBox
                              sx={{
                                width: `${Math.min(
                                  ((15000 +
                                    parseFloat(
                                      salesOrderDraft.totalAmount || '0',
                                    )) /
                                    50000) *
                                    100,
                                  100,
                                )}%`,
                                height: '100%',
                                backgroundColor: 'success.main',
                                borderRadius: '3px',
                              }}
                            />
                          </MDBox>
                          <MDBox
                            display="flex"
                            justifyContent="flex-start"
                            mt={0.5}
                          >
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Current Balance: $15,000.00
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      ) : (
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: 14 }}
                        >
                          Not available until customer is assigned
                        </MDTypography>
                      )} */}

                      <MDTypography className="label">Ship Date:</MDTypography>
                      {isModalEditable ? (
                        <DatePicker
                          className={cn(
                            salesOrderDraft.shipDate &&
                              isDatePast(salesOrderDraft.shipDate, true) &&
                              'border-red-500',
                          )}
                          placeholder="Set a ship date"
                          value={parseDateOnly(salesOrderDraft.shipDate)}
                          onSelect={handleInlineShipDateUpdate}
                          disabled={{
                            before: (() => {
                              const t = new Date();
                              t.setHours(0, 0, 0, 0);
                              return t;
                            })(),
                            after: parseDateOnly(salesOrderDraft.deliveryDate),
                          }}
                        />
                      ) : salesOrderDraft.shipDate ? (
                        <MDTypography variant="body2">
                          {parseDateOnly(
                            salesOrderDraft.shipDate,
                          )?.toLocaleDateString()}
                        </MDTypography>
                      ) : (
                        <MDBox>
                          <MDTypography variant="body2">-</MDTypography>
                        </MDBox>
                      )}

                      <MDTypography className="label">
                        Delivery Date:
                      </MDTypography>
                      {isModalEditable ? (
                        <DatePicker
                          className={cn(
                            salesOrderDraft.deliveryDate &&
                              isDatePast(salesOrderDraft.deliveryDate, true) &&
                              'border-red-500',
                          )}
                          placeholder="Set a delivery date"
                          value={parseDateOnly(salesOrderDraft.deliveryDate)}
                          onSelect={handleInlineDeliveryDateUpdate}
                          disabled={{
                            before: (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const ship = parseDateOnly(
                                salesOrderDraft.shipDate,
                              );
                              return ship && ship > today ? ship : today;
                            })(),
                          }}
                        />
                      ) : salesOrderDraft.deliveryDate ? (
                        <MDTypography variant="body2">
                          {parseDateOnly(
                            salesOrderDraft.deliveryDate,
                          )?.toLocaleDateString()}
                        </MDTypography>
                      ) : (
                        <MDBox>
                          <MDTypography variant="body2">-</MDTypography>
                        </MDBox>
                      )}

                      <MDTypography className="label">Total:</MDTypography>
                      <MDBox display="flex" flexDirection="column">
                        <MDTypography variant="body2">
                          {salesOrderDraft.totalAmount &&
                          !isNaN(parseFloat(salesOrderDraft.totalAmount))
                            ? `$${parseFloat(
                                salesOrderDraft.totalAmount,
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </MDTypography>
                        {salesOrderDraft.extractedTotalAmount &&
                          salesOrderDraft.totalAmount &&
                          parseFloat(
                            String(salesOrderDraft.extractedTotalAmount),
                          ) !==
                            parseFloat(String(salesOrderDraft.totalAmount)) && (
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic', mt: 0.25 }}
                            >
                              Note: We initially extracted a total amount of $
                              {parseFloat(
                                String(salesOrderDraft.extractedTotalAmount),
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </MDTypography>
                          )}
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Line items */}
              <MDBox
                sx={{ flex: 1, minHeight: 0 }}
                mb={showChargeModal ? 2 : 0}
              >
                {validationErrors.inactiveProducts && (
                  <MDBox mb={2}>
                    <MDTypography variant="caption" color="error">
                      {validationErrors.inactiveProducts}
                    </MDTypography>
                  </MDBox>
                )}
                <LineItemsTable
                  lineItems={displayLineItems}
                  editable={isModalEditable}
                  isLoading={isLoading}
                  onDeleteLineItem={handleDeleteLineItem}
                  onAddLineItem={handleAddLineItem}
                  onUpdateLineItem={handleUpdateLineItem}
                  onToggleRejectLineItem={handleToggleRejectLineItem}
                  pendingUpdates={pendingUpdates}
                  onSelectLineItem={
                    isAssociateProductV2Enabled
                      ? undefined
                      : (index) => {
                          setSelectedLineItemIndex(index);
                          // Capture a snapshot of the line item at the time the modal opens
                          setSelectedLineItemSnapshot({
                            ...lineItems[index],
                            ...optimisticUpdates[index],
                          });
                        }
                  }
                  customerId={customerId}
                  onAssociateVariantSelected={handleAssociateVariantSelected}
                  isEdiSource={isEdiSource}
                  isEmailSource={isEmailSource}
                  stagedPurchaseOrderId={salesOrderDraft?.id}
                />
              </MDBox>

              {/* Order-level charges */}
              {!showChargeModal ? null : (
                <MDBox mb={2}>
                  <ChargesSection
                    charges={salesOrderDraft.charges || []}
                    onAddCharge={handleAddOrderCharge}
                    onUpdateCharge={handleUpdateOrderCharge}
                    onDeleteCharge={handleDeleteOrderCharge}
                    editable={isModalEditable}
                    isLoading={
                      addOrderCharge.isLoading ||
                      updateOrderCharge.isLoading ||
                      deleteOrderCharge.isLoading
                    }
                    subtotal={displayLineItems.reduce((sum, item) => {
                      if (item.quantity && item.unitPrice) {
                        return sum + item.quantity * item.unitPrice;
                      }
                      return sum + (item.amount || 0);
                    }, 0)}
                    title="Order Charges"
                    emptyStateText="No order-level charges added"
                  />
                </MDBox>
              )}
            </Grid>

            {/* Right side - Activity Graph / Source Document Viewer */}
            {isRightPanelVisible && (
              <Grid
                item
                xs={12}
                md={isActivityLogEnabled ? 5 : 6}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                {isActivityLogEnabled ? (
                  <ActivityGraph
                    entityType={SystemEntity.AR_STAGED_SALES_ORDER}
                    entityId={salesOrderDraftId}
                    conversationId={
                      salesOrderDraft?.sourceEmail?.conversationId
                    }
                    className="h-full min-h-0"
                  />
                ) : (
                  <SourceDocumentViewer
                    sourceFlags={sourceFlags}
                    emailData={emailData}
                    conversationId={
                      salesOrderDraft?.sourceEmail?.conversationId
                    }
                    ordefulMessage={ediData.ordefulMessage}
                    document={documentData.document}
                  />
                )}
              </Grid>
            )}
          </Grid>
        ) : (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            {/* Empty state or loading placeholder */}
          </MDBox>
        )}

        {renderAssociateModal()}

        {showDeleteLineItemModal && deletingLineItem && (
          <DeleteLineItemModal
            open={showDeleteLineItemModal}
            onClose={() => {
              setShowDeleteLineItemModal(false);
              setDeletingLineItem(undefined);
              setDeletingLineItemIndex(undefined);
            }}
            onConfirm={handleConfirmDelete}
            lineItem={deletingLineItem}
            isLoading={deleteLineItem.isLoading}
            error={deleteLineItem.error || undefined}
          />
        )}

        {/* Delete confirmation dialog */}
        {showDeleteModal && (
          <Dialog
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Delete Sales Order Draft</DialogTitle>
            <DialogContent>
              <MDTypography variant="body2">
                Are you sure you want to permanently delete this sales order
                draft? This action cannot be undone.
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton
                variant="text"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteStagedPurchaseOrder.isLoading}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="contained"
                color="error"
                onClick={handleConfirmDeleteDraft}
                disabled={deleteStagedPurchaseOrder.isLoading}
              >
                {deleteStagedPurchaseOrder.isLoading ? 'Deleting...' : 'Delete'}
              </MDButton>
            </DialogActions>
          </Dialog>
        )}

        {showRejectModal && (
          <RejectSalesOrderDraftModal
            open={showRejectModal}
            onClose={() => setShowRejectModal(false)}
            onConfirm={handleConfirmReject}
            isLoading={rejectStagedPurchaseOrder.isLoading}
            error={rejectStagedPurchaseOrder.error || undefined}
          />
        )}

        {/* Re-extract Sales Order Draft Dialog */}
        {showReextractDialog &&
          emailData.inboundEmailId &&
          salesOrderDraft?.id && (
            <ReextractSalesOrderDraftDialog
              inboundEmailId={emailData.inboundEmailId}
              classificationId={
                salesOrderDraft?.sourceEmail?.classification
                  ?.classificationResult
              }
              open={showReextractDialog}
              onOpenChange={setShowReextractDialog}
              metadata={{
                customerEmailDomain,
              }}
              entityId={salesOrderDraft.id}
              entityType={SystemEntity.AR_STAGED_SALES_ORDER}
              onSuccess={() => {
                // Refresh the sales order draft data when reextraction succeeds
                refetchSalesOrderDraft();
                onUpdate?.();
                showToast({
                  title: 'Re-extraction Complete',
                  message:
                    'Sales order draft has been re-extracted successfully.',
                  color: 'success',
                  icon: 'check_circle',
                });
              }}
            />
          )}
      </MDBox>
      <SyncErrorDialog
        syncErrorState={{
          isOpen: syncErrorDialogOpen,
          message: syncErrorMessage,
          erpSystemName: erpSystemName,
        }}
        onClose={() => {
          setSyncErrorDialogOpen(false);
          setSyncErrorMessage(null);
          setErpSystemName(null);
        }}
      />
    </Modal>
  );
};

export default SalesOrderDraftModal;
